/**
 * Points Preview Dialog
 *
 * Shows expected credit consumption before question generation.
 * Matches desktop app's PointsDashboard functionality.
 */

import type { ReactNode } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useSubmitShortcut } from '@/hooks/useKeyboard';
import type { CreditBreakdown } from '@/types/credits';

export interface PointsDialogFrameProps {
  open: boolean;
  title: string;
  /** Dialog-specific content above the balance / expected-points row. */
  children?: ReactNode;
  balanceText: string;
  expectedText: string;
  /** Red balance box (expected points exceed the balance). */
  balanceShort: boolean;
  /** Warnings rendered under the balance row. */
  notice?: ReactNode;
  confirmText: string;
  confirmDisabled: boolean;
  confirmTitle?: string;
  /** Cmd/Ctrl+Enter submits only while this is true. */
  shortcutEnabled: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onPurchase: () => void;
  /** Renders the purchase control as a link to this path (still calling onPurchase). */
  purchaseHref?: string;
}

/**
 * Shared points-confirmation frame: header, body slot, balance / expected-points
 * boxes, warnings and the 취소 / 포인트 구매 / start footer. Callers own the
 * blocking rules (SPEC-ONECLICK-BILLING-001 design §12).
 */
export function PointsDialogFrame({
  open,
  title,
  children,
  balanceText,
  expectedText,
  balanceShort,
  notice,
  confirmText,
  confirmDisabled,
  confirmTitle,
  shortcutEnabled,
  onClose,
  onConfirm,
  onPurchase,
  purchaseHref,
}: PointsDialogFrameProps) {
  useSubmitShortcut(shortcutEnabled ? onConfirm : undefined, open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-forest-200 bg-forest-50">
          <h2 className="text-lg font-semibold text-forest-900">{title}</h2>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {children}

          {/* Points Summary Row */}
          <div className="flex gap-3">
            {/* Current Points Box */}
            <div
              className={`flex-1 border-2 rounded-lg p-3 ${
                balanceShort
                  ? 'bg-red-50 border-red-300'
                  : 'bg-green-50 border-green-300'
              }`}
            >
              <div
                className={`font-bold ${
                  balanceShort ? 'text-red-800' : 'text-green-800'
                }`}
              >
                {balanceText}
              </div>
            </div>

            {/* Expected Points Box */}
            <div className="flex-1 bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
              <div className="font-bold text-amber-800">{expectedText}</div>
            </div>
          </div>

          {notice}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            취소
          </Button>
          {purchaseHref ? (
            <a
              href={purchaseHref}
              onClick={(event) => {
                event.preventDefault();
                onPurchase();
              }}
              className="flex-1 inline-flex items-center justify-center rounded-lg font-medium border border-forest-600 bg-white text-forest-600 hover:bg-forest-50 px-4 py-2 text-base"
            >
              <span>포인트 구매</span>
              <ExternalLink className="h-4 w-4 ml-1" />
            </a>
          ) : (
            <Button variant="outline" className="flex-1" onClick={onPurchase}>
              <span>포인트 구매</span>
              <ExternalLink className="h-4 w-4 ml-1" />
            </Button>
          )}
          <Button
            className="flex-1"
            onClick={onConfirm}
            disabled={confirmDisabled}
            title={confirmTitle}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface PointsPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onPurchase: () => void;
  breakdown: CreditBreakdown;
  currentCredits: number | null;
  selectedTypeNames: string[];
  ebsMatchedPassageIds?: Set<string>;
  isFreeGeneration?: boolean;
  /** Extra content shown first, above the summary table (e.g. the passages being generated). */
  details?: ReactNode;
}

type CategoryKey = '문제' | '자료' | '교재' | '모의고사';

const CATEGORY_ORDER: CategoryKey[] = ['문제', '자료', '교재', '모의고사'];

export function PointsPreviewDialog({
  open,
  onClose,
  onConfirm,
  onPurchase,
  breakdown,
  currentCredits,
  selectedTypeNames,
  ebsMatchedPassageIds,
  isFreeGeneration = false,
  details = null,
}: PointsPreviewDialogProps) {
  const effectiveCredits = currentCredits ?? 0;
  const isInsufficient = !isFreeGeneration && breakdown.totalCredits > effectiveCredits;
  const isZeroCredits = effectiveCredits <= 0 && !isFreeGeneration;
  const hasEbsIneligibleTypes = (breakdown.ebsIneligibleTypes?.length ?? 0) > 0;

  // Filter categories with data
  const categoriesWithData = CATEGORY_ORDER.filter((category) => {
    const data = breakdown[category];
    return data.questions > 0;
  });

  return (
    <PointsDialogFrame
      open={open}
      title="출제 예정 포인트"
      balanceText={`현재 남은 포인트: ${effectiveCredits.toLocaleString()} 포인트`}
      expectedText={`소진 예정 포인트: ${breakdown.totalCredits.toLocaleString()} 포인트`}
      balanceShort={isInsufficient}
      confirmText="출제 시작"
      confirmDisabled={isZeroCredits || isInsufficient}
      confirmTitle={isZeroCredits || isInsufficient ? '포인트를 충전한 후 출제를 시작해주세요' : undefined}
      shortcutEnabled={!isZeroCredits && !isInsufficient}
      onClose={onClose}
      onConfirm={onConfirm}
      onPurchase={onPurchase}
      notice={
        <>
          {/* Insufficient Credits Warning */}
          {isInsufficient && (
            <div className="flex items-start gap-2 text-red-700 text-sm">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                {isZeroCredits
                  ? '포인트가 없습니다. 포인트를 충전한 후 출제를 시작해주세요.'
                  : '포인트가 부족합니다. 포인트를 충전하거나 출제할 항목을 줄인 후 다시 확인해주세요.'}
              </div>
            </div>
          )}

          {/* Estimate Warning */}
          <div className="text-xs text-gray-500 italic">
            ※ 확인한 문항별 예정 금액으로 차감됩니다. 실패·취소·건너뛴 항목은 차감되지 않습니다.
          </div>
        </>
      }
    >
          {details}

          {/* Summary Table */}
          <div className="bg-gray-50 rounded-lg overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-2 bg-gray-200 px-3 py-2 text-sm font-semibold text-forest-900">
              <div></div>
              <div className="text-center">지문 수</div>
              <div className="text-center">유형 수</div>
              <div className="text-center">전체 문제 수</div>
            </div>

            {/* Table Rows */}
            {categoriesWithData.map((category) => {
              const data = breakdown[category];
              const isMocktest = category === '모의고사';

              return (
                <div
                  key={category}
                  className="grid grid-cols-4 gap-2 px-3 py-2 text-sm border-t border-gray-200 bg-white"
                >
                  <div className="font-medium text-forest-900">{category}:</div>
                  <div className="text-center text-forest-700">
                    {/* 모의고사 hides 지문 수 */}
                    {isMocktest ? '' : data.passages}
                  </div>
                  <div className="text-center text-forest-700">
                    {/* 모의고사 hides 유형 수 */}
                    {data.types === null ? '' : data.types}
                  </div>
                  <div className="text-center text-forest-700">
                    {data.questions}
                  </div>
                </div>
              );
            })}

            {/* Footer Row - Totals */}
            <div className="grid grid-cols-4 gap-2 px-3 py-2 text-sm border-t-2 border-gray-300 bg-gray-100">
              <div className="font-bold text-forest-900">합계:</div>
              <div className="text-center"></div>
              <div className="text-center"></div>
              <div className="text-center font-bold text-forest-900">
                {breakdown.totalQuestions}
              </div>
            </div>
          </div>

          {/* Selected Types */}
          <div>
            <div className="text-sm font-semibold text-forest-900 mb-2">
              *선택된 유형:
            </div>
            <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
              {selectedTypeNames.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedTypeNames.map((typeName, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-forest-100 text-forest-800 border border-forest-200"
                    >
                      {typeName}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-forest-700">
                  선택된 유형이 없습니다.
                </span>
              )}
            </div>
          </div>

          {/* EBS Matched Passages */}
          {ebsMatchedPassageIds && ebsMatchedPassageIds.size > 0 && (
            <div>
              <div className="text-sm font-semibold text-green-800 mb-2">
                *EBS 매칭 지문 ({ebsMatchedPassageIds.size}개):
              </div>
              <div className="bg-green-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {Array.from(ebsMatchedPassageIds).sort().map((pid) => (
                    <span
                      key={pid}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 border border-green-200"
                    >
                      {pid}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-xs text-green-700 mt-1 ml-1">
                → EBS 자료를 사용하는 항목은 1포인트이며, 나머지는 유형별 예정 금액이 적용됩니다
                {hasEbsIneligibleTypes && (
                  <span className="text-amber-700"> (옵션 설정 유형 제외)</span>
                )}
              </div>

              {/* EBS discount ineligible types warning */}
              {hasEbsIneligibleTypes && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-amber-800 mb-1.5">
                    * 옵션이 설정되어 EBS 할인이 적용되지 않는 유형:
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {breakdown.ebsIneligibleTypes!.map((typeName) => (
                      <span
                        key={typeName}
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300"
                      >
                        {typeName}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-amber-700 mt-1.5">
                    → 위 유형은 옵션 설정으로 인해 매칭된 지문이어도 정가로 처리됩니다
                  </div>
                </div>
              )}
            </div>
          )}
    </PointsDialogFrame>
  );
}
